import { useState, useEffect } from 'react';
import {
  Mail,
  Clock,
  RefreshCw,
  Send,
  XCircle,
  CheckCircle,
  AlertCircle,
  Crown,
  Shield,
  Wifi,
} from 'lucide-react';
import DataTable from '../common/DataTable';
import type { Column } from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import { userService, type Invitation } from '../../services';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

interface PendingInvitationsTabProps {
  tenantId?: string;
}

const PendingInvitationsTab = ({ tenantId }: PendingInvitationsTabProps) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  // Modal states
  const [resendInvitation, setResendInvitation] = useState<Invitation | undefined>();
  const [revokeInvitation, setRevokeInvitation] = useState<Invitation | undefined>();
  const [isResending, setIsResending] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const params: { tenantId?: string; status?: string } = {};
      if (tenantId) {
        params.tenantId = tenantId;
      }
      const result = await userService.listInvitations(params);
      setInvitations(result.invitations || []);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      toast.error('Load Failed', 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [tenantId]);

  const handleRefresh = () => {
    fetchInvitations();
  };

  const handleResendClick = (invitation: Invitation) => {
    setResendInvitation(invitation);
  };

  const handleResendConfirm = async () => {
    if (!resendInvitation) return;

    try {
      setIsResending(true);
      await userService.resendInvitation(resendInvitation.id);
      toast.success('Invitation Resent', `Invitation resent to ${resendInvitation.email}`);
      await fetchInvitations();
      setResendInvitation(undefined);
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error('Resend Failed', 'Failed to resend invitation');
    } finally {
      setIsResending(false);
    }
  };

  const handleRevokeClick = (invitation: Invitation) => {
    setRevokeInvitation(invitation);
  };

  const handleRevokeConfirm = async () => {
    if (!revokeInvitation) return;

    try {
      setIsRevoking(true);
      await userService.revokeInvitation(revokeInvitation.id);
      toast.success('Invitation Revoked', `Invitation for ${revokeInvitation.email} has been revoked`);
      await fetchInvitations();
      setRevokeInvitation(undefined);
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      toast.error('Revoke Failed', 'Failed to revoke invitation');
    } finally {
      setIsRevoking(false);
    }
  };

  // Get user type label and icon
  const getUserTypeDisplay = (userType: number) => {
    switch (userType) {
      case 1:
        return { label: 'Super Admin', icon: Crown, color: 'amber' };
      case 2:
        return { label: 'Tenant Admin', icon: Shield, color: 'primary' };
      case 3:
        return { label: 'VPN User', icon: Wifi, color: 'teal' };
      // Type 5 can't be invited — the provisioning endpoint rejects it, so a
      // ChannelAdmin is always created out-of-band. Handled for completeness
      // so a stray legacy row doesn't render as "Unknown".
      case 5:
        return { label: 'Channel Admin', icon: Shield, color: 'primary' };
      default:
        return { label: 'Unknown', icon: AlertCircle, color: 'gray' };
    }
  };

  // Get status badge.
  // Note: 'sent' is an internal sub-state of pending (email delivery confirmed).
  // We display it as "Pending" so the UI matches the 4 documented states:
  // Pending / Accepted / Expired / Revoked.
  const getStatusBadge = (status: Invitation['status']) => {
    switch (status) {
      case 'pending':
      case 'sent':
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock };
      case 'accepted':
        return { label: 'Accepted', color: 'bg-success-100 text-success-700', icon: CheckCircle };
      case 'expired':
        return { label: 'Expired', color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
      case 'revoked':
        return { label: 'Revoked', color: 'bg-red-100 text-red-700', icon: XCircle };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
    }
  };

  // Check if invitation is expired
  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  // Table columns
  const columns: Column<Invitation>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (inv) => (
        <div className="flex items-center space-x-2">
          <Mail className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{inv.email}</span>
        </div>
      ),
    },
    {
      key: 'userType',
      label: 'Type',
      sortable: true,
      render: (inv) => {
        const { label, icon: Icon, color } = getUserTypeDisplay(inv.userType);
        return (
          <div className="flex items-center space-x-1">
            <Icon className={`w-3 h-3 text-${color}-500`} />
            <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${color}-100 text-${color}-700`}>
              {label}
            </span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (inv) => {
        const actualStatus = isExpired(inv.expiresAt) && inv.status !== 'accepted' && inv.status !== 'revoked'
          ? 'expired'
          : inv.status;
        const { label, color, icon: Icon } = getStatusBadge(actualStatus);
        return (
          <div className="flex items-center space-x-1">
            <Icon className="w-3 h-3" />
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
              {label}
            </span>
          </div>
        );
      },
    },
    {
      key: 'inviterName',
      label: 'Invited By',
      render: (inv) => <span className="text-sm text-gray-600">{inv.inviterName || '-'}</span>,
    },
    {
      key: 'expiresAt',
      label: 'Expires',
      sortable: true,
      render: (inv) => {
        const expired = isExpired(inv.expiresAt);
        return (
          <span className={`text-sm ${expired ? 'text-red-600' : 'text-gray-600'}`}>
            {formatDate(inv.expiresAt, useSettingsStore.getState().getDateFormatSettings())}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Sent',
      render: (inv) => (
        <span className="text-sm text-gray-500">
          {formatDate(inv.sentAt || inv.createdAt, useSettingsStore.getState().getDateFormatSettings())}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (inv) => {
        const canResend = inv.status === 'pending' || inv.status === 'sent';
        const canRevoke = inv.status !== 'accepted' && inv.status !== 'revoked';

        return (
          <div className="flex justify-end space-x-2">
            {canResend && (
              <button
                onClick={() => handleResendClick(inv)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Resend Invitation"
              >
                <Send className="w-4 h-4 text-primary-600" />
              </button>
            )}
            {canRevoke && (
              <button
                onClick={() => handleRevokeClick(inv)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Revoke Invitation"
              >
                <XCircle className="w-4 h-4 text-gray-600 hover:text-red-600" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Stats — the list is server-filtered to pending/sent only (accepted/revoked/expired
  // rows are hidden once they transition out of the active state). So counters only
  // reflect the active invitation set.
  const stats = {
    total: invitations.length,
    pending: invitations.filter(
      (i) => (i.status === 'pending' || i.status === 'sent') && !isExpired(i.expiresAt),
    ).length,
    expired: invitations.filter((i) => isExpired(i.expiresAt)).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Track and manage pending user invitations
          </p>
        </div>
        <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats — reflect only active (not yet accepted/revoked) invitations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Mail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Active</p>
              <p className="text-xl font-semibold">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-xl font-semibold">{stats.pending}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-xl font-semibold">{stats.expired}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={invitations}
          searchPlaceholder="Search invitations..."
          loading={loading}
        />
      </div>

      {/* Resend Confirmation */}
      <ConfirmDialog
        isOpen={!!resendInvitation}
        onClose={() => setResendInvitation(undefined)}
        onConfirm={handleResendConfirm}
        title="Resend Invitation"
        message={`Are you sure you want to resend the invitation to "${resendInvitation?.email}"?\n\nA new invitation email will be sent.`}
        confirmText="Resend"
        cancelText="Cancel"
        variant="info"
        isLoading={isResending}
      />

      {/* Revoke Confirmation */}
      <ConfirmDialog
        isOpen={!!revokeInvitation}
        onClose={() => setRevokeInvitation(undefined)}
        onConfirm={handleRevokeConfirm}
        title="Revoke Invitation"
        message={`Are you sure you want to revoke the invitation for "${revokeInvitation?.email}"?\n\nThey will no longer be able to use this invitation link.`}
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
        isLoading={isRevoking}
      />
    </div>
  );
};

export default PendingInvitationsTab;
